import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  useLayoutEffect,
} from 'react';
import { getThemeStyles } from '../theme';

const STORAGE_KEY = 'trifusion-theme';

const ThemeContext = createContext(null);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

/**
 * Theme-aware style object (navbar/content/text colors). Prefer over getThemeStyles()
 * so components re-render when the user toggles dark/light.
 */
export const useThemeStyles = () => {
  const { theme } = useTheme();
  return useMemo(() => getThemeStyles(theme), [theme]);
};

function readStoredTheme() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === 'light' || raw === 'dark') return raw;
  } catch (_) {
    /* ignore */
  }
  return 'dark';
}

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(readStoredTheme);

  const setTheme = useCallback((next) => {
    const value = typeof next === 'function' ? next(theme) : next;
    if (value !== 'dark' && value !== 'light') return;
    setThemeState(value);
    try {
      localStorage.setItem(STORAGE_KEY, value);
    } catch (_) {
      /* ignore */
    }
  }, [theme]);

  const toggleTheme = useCallback(() => {
    setTheme((t) => (t === 'dark' ? 'light' : 'dark'));
  }, [setTheme]);

  useLayoutEffect(() => {
    const root = document.documentElement;
    root.dataset.theme = theme;
    document.body.classList.remove('dark-theme', 'light-theme');
    document.body.classList.add(theme === 'dark' ? 'dark-theme' : 'light-theme');
  }, [theme]);

  const value = useMemo(
    () => ({
      theme,
      setTheme,
      toggleTheme,
    }),
    [theme, setTheme, toggleTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};
